import { HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  GetMemberPrivacySettingsResponse,
  MemberPrivacySettings,
  SetMemberPrivacySettingsApplicationErrors,
  SetMemberPrivacySettingsResponse,
  getMemberPrivacySettings as universalGetMemberPrivacySettings,
  setMemberPrivacySettings as universalSetMemberPrivacySettings,
} from './members-v1-member-privacy-settings-member-privacy-settings.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/members' };

export function getMemberPrivacySettings(
  httpClient: HttpClient
): GetMemberPrivacySettingsSignature {
  return () =>
    universalGetMemberPrivacySettings(
      // @ts-ignore
      { httpClient }
    );
}

interface GetMemberPrivacySettingsSignature {
  /**
   * Retrieves the member privacy settings for a site.
   *
   * If no settings were set, the default settings are retrieved.
   *
   * >**Note:**
   * >This method requires [visitor or member authentication](https://dev.wix.com/docs/api-reference/articles/authentication/about-identities).
   */
  (): Promise<
    NonNullablePaths<
      GetMemberPrivacySettingsResponse,
      | `memberPrivacySettings.publicMemberVisibility`
      | `memberPrivacySettings.publicMemberCandidates`,
      3
    >
  >;
}

export function setMemberPrivacySettings(
  httpClient: HttpClient
): SetMemberPrivacySettingsSignature {
  return (
    memberPrivacySettings: NonNullablePaths<
      MemberPrivacySettings,
      `revision`,
      2
    >
  ) =>
    universalSetMemberPrivacySettings(
      memberPrivacySettings,
      // @ts-ignore
      { httpClient }
    );
}

interface SetMemberPrivacySettingsSignature {
  /**
   * Sets the member privacy settings for a site.
   *
   * Each time the member privacy settings are updated, `revision` increments by 1. The existing `revision` must be included when updating the member privacy settings. This ensures you're working with the latest member privacy settings and prevents unintended overwrites.
   * @param - Member privacy settings details.
   */
  (
    memberPrivacySettings: NonNullablePaths<
      MemberPrivacySettings,
      `revision`,
      2
    >
  ): Promise<
    NonNullablePaths<
      SetMemberPrivacySettingsResponse,
      | `memberPrivacySettings.publicMemberVisibility`
      | `memberPrivacySettings.publicMemberCandidates`,
      3
    > & {
      __applicationErrorsType?: SetMemberPrivacySettingsApplicationErrors;
    }
  >;
}

export {
  AccountInfo,
  ActionEvent,
  Asset,
  BulkGetMemberPrivacySettingsRequest,
  BulkGetMemberPrivacySettingsResponse,
  Candidates,
  DeleteContext,
  DeleteStatus,
  DomainEvent,
  DomainEventBodyOneOf,
  EditorlessAssigned,
  EditorlessUnassigned,
  Empty,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  GetMemberPrivacySettingsRequest,
  GetMemberPrivacySettingsResponse,
  IdentificationData,
  IdentificationDataIdOneOf,
  InvalidRevisionPayload,
  InvalidSettingsPayload,
  MemberPrivacySettings,
  MessageEnvelope,
  MetaSiteSpecialEvent,
  MetaSiteSpecialEventPayloadOneOf,
  Namespace,
  NamespaceChanged,
  OdeditorAssigned,
  OdeditorUnassigned,
  PicassoAssigned,
  PicassoUnassigned,
  RestoreInfo,
  ServiceProvisioned,
  ServiceRemoved,
  SetMemberPrivacySettingsRequest,
  SetMemberPrivacySettingsResponse,
  SiteCreated,
  SiteCreatedContext,
  SiteDeleted,
  SiteHardDeleted,
  SiteMarkedAsTemplate,
  SiteMarkedAsWixSite,
  SiteMemberInstanceMemberPrivacySettings,
  SitePublished,
  SitePurgedExternally,
  SiteRenamed,
  SiteTransferred,
  SiteUndeleted,
  SiteUnpublished,
  SiteUrlChanged,
  State,
  StudioAssigned,
  StudioTwoAssigned,
  StudioTwoUnassigned,
  StudioUnassigned,
  UserDomainMediaDisabled,
  UserDomainMediaEnabled,
  Visibility,
  WebhookIdentityType,
  WixelAssigned,
  WixelUnassigned,
} from './members-v1-member-privacy-settings-member-privacy-settings.universal.js';
